import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * Shipment : famille "mouvement physique de biens".
 *
 * Famille du socle sur le modèle "famille + type" (cf. asset, le patron). Un
 * shipment est le déplacement concret de marchandises vers ou depuis un tiers :
 * expédition d'un colis, livraison sur site, retrait au comptoir, dépose ou
 * reprise de matériel, retour d'un article, avec suivi et preuve de remise. Le
 * vocabulaire métier vit dans `type` (chaîne libre déclarée par l'app :
 * "delivery", "pickup", "return"...), jamais dans le nom de collection.
 *
 * Le SENS du mouvement (vers le tiers ou depuis le tiers) vit dans `direction`,
 * l'ÉTAT d'avancement (préparé, en transit, livré, échoué) dans `status` : ce
 * sont des états métier, distincts du discriminant `type`.
 *
 * Les liens vers d'autres familles (order, stock-movement, route, inspection,
 * contact) sont de simples ids : aucune dépendance de schéma entre familles.
 * Tous restent optionnels (un retour peut n'être rattaché à aucune commande) ;
 * seul le discriminant `type` est requis à la création.
 */

/** Champs métier de la famille shipment (hors champs standard du socle). */
export const shipmentCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "delivery", "pickup", "return"). */
  type: z.string().min(1),
  /** Commande `order` que le mouvement exécute (livraison ou réception), optionnel. */
  orderId: z.string().min(1).optional(),
  /** Sens du mouvement, chaîne libre pilotée par l'app (ex. "outbound", "inbound"). */
  direction: z.string().min(1).optional(),
  /** Transporteur en charge de l'acheminement (ex. "DHL", "interne"), optionnel. */
  carrier: z.string().min(1).optional(),
  /** Numéro de suivi fourni par le transporteur, optionnel. */
  trackingNumber: z.string().min(1).optional(),
  /** Date prévue d'enlèvement ou de remise, optionnel. */
  scheduledAt: z.date().optional(),
  /** Date effective de remise (preuve de livraison), optionnel. */
  deliveredAt: z.date().optional(),
  /** Référence de destination (adresse, point relais, lieu), chaîne libre, optionnel. */
  destinationRef: z.string().min(1).optional(),
  /** État d'avancement, chaîne libre (ex. "prepare", "in_transit", "delivered", "failed"). */
  status: z.string().min(1).optional(),
  /** Mouvement de stock `stock-movement` associé à la sortie ou l'entrée, optionnel. */
  stockMovementId: z.string().min(1).optional(),
  /** Tournée `route` qui regroupe le mouvement en arrêt ordonné, optionnel. */
  routeId: z.string().min(1).optional(),
  /** Constat `inspection` réalisé à la remise, optionnel. */
  inspectionId: z.string().min(1).optional(),
  /** Fiche `contact` du destinataire ou de l'expéditeur tiers, optionnel. */
  contactId: z.string().min(1).optional(),
} as const;

/** Schéma complet de la famille shipment (champs standard + champs métier). */
export const ShipmentSchema = z.object({
  ...baseEntityFields,
  ...shipmentCoreFields,
});

/** Type d'un shipment. */
export type Shipment = z.infer<typeof ShipmentSchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const ShipmentCreateInputSchema = z.object({
  ...shipmentCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'un shipment. */
export type ShipmentCreateInput = z.infer<typeof ShipmentCreateInputSchema>;

/**
 * Schema du patch de mise a jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont geres par le SDK : aucun des deux n'est accepte ici.
 */
export const ShipmentUpdateInputSchema = z
  .object({ ...shipmentCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise a jour d'un shipment. */
export type ShipmentUpdateInput = z.infer<typeof ShipmentUpdateInputSchema>;
